import { useMemo, useState, type ReactNode } from "react";

import { shortcutCatalog, type ShortcutGroup } from "./catalog";
import { Keys } from "./Keys";

function filterGroups(groups: ShortcutGroup[], query: string): ShortcutGroup[] {
  const needle = query.trim().toLowerCase();
  if (needle === "") return groups;
  return groups
    .map((group) => ({
      name: group.name,
      entries: group.entries.filter(
        (entry) =>
          entry.action.toLowerCase().includes(needle) ||
          entry.keys.toLowerCase().includes(needle) ||
          group.name.toLowerCase().includes(needle),
      ),
    }))
    .filter((group) => group.entries.length > 0);
}

/** The filterable shortcut table; the Help dialog shows it on its "Keyboard shortcuts" tab. */
export function ShortcutsGuide(): ReactNode {
  const [query, setQuery] = useState("");
  const groups = useMemo(() => filterGroups(shortcutCatalog(), query), [query]);

  return (
    <div>
      <label className="block">
        <span className="sr-only">Filter shortcuts</span>
        <input
          type="search"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
          }}
          placeholder="Filter shortcuts…"
          className="w-full rounded-md border border-line bg-canvas px-3 py-2 text-sm text-ink"
        />
      </label>

      <div className="mt-4 max-h-[55vh] overflow-y-auto pr-1">
        {groups.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted">No shortcuts match “{query}”.</p>
        ) : (
          groups.map((group) => (
            <section key={group.name} className="mb-5">
              <h3 className="mb-2 text-xs font-semibold tracking-wide text-muted uppercase">
                {group.name}
              </h3>
              <table className="w-full border-collapse text-sm">
                <tbody>
                  {group.entries.map((entry) => (
                    <tr key={`${group.name}-${entry.keys}-${entry.action}`} className="align-top">
                      <th scope="row" className="w-2/3 py-1 pr-4 text-left font-normal">
                        {entry.action}
                        {entry.implemented !== true && (
                          <span className="ml-2 text-xs text-muted">(not yet wired up)</span>
                        )}
                      </th>
                      <td className="py-1">
                        <Keys keys={entry.keys} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          ))
        )}
      </div>
    </div>
  );
}
